<template>
  <!-- The marketing site's header (improve-online src/components/Header.vue),
       brought to the app's public pages (signup, Cyber Security) on 5 Oct 2026
       so they look like the site they are reached from. Every link but Login
       goes to the marketing site; there is no search box (that searches the
       marketing site's own pages). Own class names: the app's global
       stylesheet still styles the old header's .page-header, .benefits-bar
       and .benefit-item. -->
  <header class="site-header">
    <div class="site-header__top">
      <v-container class="site-header__inner">
        <div class="site-header__top-row">
          <a :href="mailto" class="site-header__email">
            <img src="/static/icons/email-improve-microlearning-platform.png" alt="" class="site-header__email-icon" />
            <span>info@improve.online</span>
          </a>

          <div class="site-header__top-right">
            <div class="site-header__top-links">
              <a :href="site('/about-us')">Our Story</a>
              <span class="site-header__sep">|</span>
              <a :href="site('/partner-programme')">Partners</a>
              <span class="site-header__sep">|</span>
              <a :href="site('/resources')">Resources</a>
            </div>
            <router-link to="/login" class="site-header__pill site-header__pill--teal">Login</router-link>
            <a :href="partnerUrl" class="site-header__pill site-header__pill--navy site-header__partner">Partner Login</a>
          </div>
        </div>
      </v-container>
    </div>

    <div class="site-header__bar">
      <v-container class="site-header__inner">
        <div class="site-header__bar-row">
          <a :href="site('/homepage')" class="site-header__brand">
            <img src="/static/brand/logo.png" alt="IMPROVE" class="site-header__logo" />
          </a>

          <nav class="site-header__nav" aria-label="Primary">
            <a v-for="item in primary" :key="item.path" :href="site(item.path)" class="site-header__link">{{ item.label }}</a>
            <div class="site-header__more" @mouseenter="openMore" @mouseleave="scheduleCloseMore">
              <button
                type="button"
                class="site-header__more-btn"
                :aria-expanded="String(moreOpen)"
                @click.stop="toggleMore"
              >
                More <span aria-hidden="true">▾</span>
              </button>
              <div v-show="moreOpen" class="site-header__more-menu">
                <a v-for="item in more" :key="item.path" :href="site(item.path)">{{ item.label }}</a>
              </div>
            </div>
            <a :href="site('/watch-demo')" class="site-header__link site-header__link--demo">Watch Demo</a>
            <a :href="site('/experience')" class="site-header__cta site-header__cta--teal">Experience IMPROVE</a>
            <a :href="site('/get-started')" class="site-header__cta site-header__cta--navy">Buy IMPROVE</a>
          </nav>

          <!-- Both conversion buttons stay visible below desktop width, as on
               the marketing site. -->
          <div class="site-header__compact">
            <div class="site-header__compact-ctas">
              <a :href="site('/experience')" class="site-header__cta site-header__cta--teal">Experience IMPROVE</a>
              <a :href="site('/get-started')" class="site-header__cta site-header__cta--navy">Buy IMPROVE</a>
            </div>
            <button
              type="button"
              class="site-header__burger"
              :aria-expanded="String(mobileOpen)"
              aria-label="Open menu"
              @click="mobileOpen = !mobileOpen"
            >
              <span /><span /><span />
            </button>
          </div>
        </div>
      </v-container>

      <div v-if="mobileOpen" class="site-header__drawer">
        <v-container class="site-header__inner">
          <ul class="site-header__drawer-list">
            <li v-for="item in drawer" :key="item.path">
              <a :href="site(item.path)">{{ item.label }}</a>
            </li>
          </ul>
        </v-container>
      </div>
    </div>

    <!-- The IMPROVE framework strip, as on every marketing page. -->
    <div class="site-header__pillars-bar">
      <v-container class="site-header__inner">
        <div class="site-header__pillars" aria-label="The IMPROVE framework">
          <div v-for="pillar in pillars" :key="pillar.word" class="site-header__pillar">
            <i :class="['fas', pillar.icon]" aria-hidden="true"></i>
            <span>{{ pillar.word }}</span>
          </div>
        </div>
      </v-container>
    </div>
  </header>
</template>

<script>
const PRIMARY = [
  { label: "Solutions", path: "/solutions" },
  { label: "Platform", path: "/features" },
  { label: "Library", path: "/course-library" },
  { label: "Pricing", path: "/pricing" },
  { label: "Stories", path: "/customer-stories" }
];

const MORE = [
  { label: "Leadership Plus", path: "/leadership-plus" },
  { label: "Compare IMPROVE", path: "/compare" },
  { label: "Why Switch", path: "/why-switch" },
  { label: "Contact", path: "/contact" },
  { label: "FAQs", path: "/faq" }
];

// The marketing site's phone menu, in its order.
const DRAWER = PRIMARY.concat(MORE.slice(0, 3), [
  { label: "Partners", path: "/partner-programme" },
  { label: "Resources", path: "/resources" },
  { label: "Our Story", path: "/about-us" },
  { label: "Contact", path: "/contact" },
  { label: "FAQs", path: "/faq" },
  { label: "Watch the 4-minute demo", path: "/watch-demo" },
  { label: "Experience IMPROVE", path: "/experience" },
  { label: "Buy IMPROVE", path: "/get-started" }
]);

const PILLARS = [
  { word: "ONBOARD", icon: "fa-user-plus" },
  { word: "TRAIN", icon: "fa-graduation-cap" },
  { word: "DEVELOP", icon: "fa-seedling" },
  { word: "MEASURE", icon: "fa-chart-bar" }
];

export default {
  name: "SignupNavigation",
  props: {
    frontEndUrl: String,
    partnerUrl: String
  },
  data() {
    return {
      primary: PRIMARY,
      more: MORE,
      drawer: DRAWER,
      pillars: PILLARS,
      moreOpen: false,
      mobileOpen: false,
      moreCloseTimer: null,
      mailto:
        "mailto:info@improve.online?subject=Customer%20Inquiry&body=Hello%20Improve%20Team,%0A%0AI%20would%20like%20to%20inquire%20about%20..."
    };
  },
  mounted() {
    document.addEventListener("click", this.onDocumentClick);
  },
  beforeDestroy() {
    this.clearMoreTimer();
    document.removeEventListener("click", this.onDocumentClick);
  },
  methods: {
    site(path) {
      return (this.frontEndUrl || "") + path;
    },
    clearMoreTimer() {
      if (this.moreCloseTimer) {
        clearTimeout(this.moreCloseTimer);
        this.moreCloseTimer = null;
      }
    },
    openMore() {
      this.clearMoreTimer();
      this.moreOpen = true;
    },
    // A short grace period so moving from "More" into its menu keeps it open.
    scheduleCloseMore() {
      this.clearMoreTimer();
      this.moreCloseTimer = setTimeout(() => {
        this.moreOpen = false;
        this.moreCloseTimer = null;
      }, 180);
    },
    toggleMore() {
      this.clearMoreTimer();
      this.moreOpen = !this.moreOpen;
    },
    onDocumentClick(e) {
      const wrap = this.$el && this.$el.querySelector(".site-header__more");
      if (this.moreOpen && wrap && !wrap.contains(e.target)) {
        this.clearMoreTimer();
        this.moreOpen = false;
      }
    }
  }
};
</script>

<style scoped>
.site-header {
  position: sticky;
  top: 0;
  z-index: 100;
  width: 100%;
  background: #fff;
  font-family: "Sora", sans-serif;
  box-shadow: 0 1px 0 #e6ebf1;
  text-align: left;
}

/* Vuetify gives every container 12px all round; the bars set their own height. */
.site-header__inner {
  max-width: 1420px;
  padding: 0 16px;
}

.site-header__top {
  border-bottom: 1px solid #eef1f5;
}

.site-header__top-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 48px;
  gap: 12px;
}

.site-header__email {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #002458;
  text-decoration: none;
  font-size: 13px;
  font-weight: 700;
}

.site-header__email-icon {
  width: 24px;
  height: 16px;
  object-fit: contain;
}

.site-header__top-right {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.site-header__top-links {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  font-size: 13px;
}

.site-header__top-links a {
  color: #334155;
  text-decoration: none;
  font-weight: 500;
  padding: 0 4px;
}

.site-header__sep {
  color: #cbd5e1;
}

.site-header__pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 0 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  text-decoration: none;
  color: #fff !important;
}

.site-header__pill--teal {
  background: #08a0a8;
}

.site-header__pill--navy {
  background: #002458;
}

.site-header__bar {
  position: relative;
  background: #fff;
}

.site-header__bar-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  min-height: 64px;
}

.site-header__brand {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
}

.site-header__logo {
  display: block;
  height: 48px;
  width: auto;
  max-width: 200px;
  object-fit: contain;
}

.site-header__nav {
  display: none;
  align-items: center;
  justify-content: flex-end;
  flex: 1 1 auto;
  gap: 4px;
  min-width: 0;
}

.site-header__link,
.site-header__more-btn {
  flex: 0 0 auto;
  color: #002458;
  text-decoration: none;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 9px;
  white-space: nowrap;
  border: 0;
  background: transparent;
  border-bottom: 2px solid transparent;
  font-family: inherit;
  cursor: pointer;
  line-height: 1.2;
}

.site-header__link:hover,
.site-header__more-btn:hover {
  border-bottom-color: #08a0a8;
}

.site-header__link--demo {
  color: #08a0a8;
}

.site-header__more {
  position: relative;
  /* Bridges the gap so moving from "More" into the menu does not close it. */
  padding-bottom: 14px;
  margin-bottom: -14px;
}

.site-header__more-menu {
  position: absolute;
  top: calc(100% - 10px);
  right: 0;
  min-width: 200px;
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 10px;
  box-shadow: 0 12px 28px rgba(0, 36, 88, 0.12);
  padding: 8px 0;
  z-index: 120;
  display: flex;
  flex-direction: column;
}

.site-header__more-menu a {
  display: block;
  padding: 10px 16px;
  color: #002458;
  text-decoration: none;
  font-size: 13px;
  font-weight: 600;
}

.site-header__more-menu a:hover {
  background: #eef8f8;
  color: #08a0a8;
}

.site-header__cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 12px;
  border: 0;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;
  margin-left: 4px;
  color: #fff;
}

.site-header__cta--teal {
  background: #08a0a8;
}

.site-header__cta--navy {
  background: #002458;
}

.site-header__compact {
  display: flex;
  align-items: center;
  gap: 10px;
}

.site-header__compact-ctas {
  display: flex;
  align-items: center;
  gap: 6px;
}

.site-header__compact-ctas .site-header__cta {
  height: 34px;
  margin-left: 0;
}

.site-header__burger {
  width: 42px;
  height: 36px;
  border: 0;
  border-radius: 6px;
  background: #08a0a8;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  cursor: pointer;
  padding: 0;
}

.site-header__burger span {
  display: block;
  width: 18px;
  height: 2px;
  background: #fff;
  border-radius: 1px;
}

.site-header__drawer {
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  background: #fff;
  border-bottom: 1px solid #e6ebf1;
  box-shadow: 0 16px 32px rgba(0, 36, 88, 0.12);
  z-index: 110;
  max-height: min(70vh, 520px);
  overflow: auto;
}

.site-header__drawer-list {
  list-style: none;
  margin: 0;
  padding: 8px 0 16px;
}

.site-header__drawer-list a {
  display: block;
  padding: 12px 4px;
  color: #002458;
  font-weight: 600;
  font-size: 15px;
  text-decoration: none;
  border-bottom: 1px solid #f1f5f9;
}

.site-header__drawer-list a:hover {
  color: #08a0a8;
}

.site-header__pillars-bar {
  background: #f5f5f5;
  border-top: 1px solid #eef1f5;
}

/* Four equal cells divided by hairlines, icon and word centred: one
   framework rather than four words spread across the page. */
.site-header__pillars {
  display: flex;
  align-items: center;
  max-width: 880px;
  margin: 0 auto;
  min-height: 46px;
}

.site-header__pillar {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 0;
  gap: 8px;
  padding: 10px 6px;
  border-left: 1px solid #d9dee5;
}

.site-header__pillar:first-child {
  border-left: 0;
}

.site-header__pillar i {
  font-size: 15px;
  color: #08a0a8;
  line-height: 1;
}

.site-header__pillar span {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.14em;
  color: #002458;
  white-space: nowrap;
}

@media (min-width: 1280px) {
  .site-header__nav {
    display: flex;
  }

  .site-header__compact {
    display: none;
  }
}

@media (max-width: 959px) {
  .site-header__top-links,
  .site-header__email {
    display: none;
  }

  .site-header__pillar span {
    font-size: 11px;
    letter-spacing: 0.1em;
  }
}

/* Phones: the logo and burger share the first row and the two buttons take
   the second, half each, so nothing is pushed off the right edge. */
@media (max-width: 599px) {
  .site-header__partner {
    display: none;
  }

  .site-header__logo {
    height: 40px;
  }

  .site-header__bar-row {
    flex-wrap: wrap;
    row-gap: 8px;
    min-height: 56px;
    padding-bottom: 8px;
  }

  .site-header__compact {
    display: contents;
  }

  .site-header__compact-ctas {
    flex: 1 1 100%;
    order: 5;
    gap: 8px;
  }

  .site-header__compact-ctas .site-header__cta {
    flex: 1 1 0;
    padding: 0 6px;
  }

  .site-header__pillar {
    flex-direction: column;
    gap: 4px;
    padding: 8px 2px;
  }

  .site-header__pillar i {
    font-size: 14px;
  }

  .site-header__pillar span {
    font-size: 9px;
    letter-spacing: 0.08em;
  }
}
</style>
